import React, {
	memo,
	useEffect, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';

const AUDIO_ANCHOR_ID = 'audio-spotlight-anchor';

const getAudioAnchor = (): HTMLDivElement => {
	let anchor = document.getElementById(AUDIO_ANCHOR_ID) as HTMLDivElement | null;
	if (!anchor) {
		anchor = document.createElement('div');
		anchor.id = AUDIO_ANCHOR_ID;
		// Visually hidden but NOT display:none — display:none can cause browsers to suspend audio
		anchor.style.cssText = 'position:fixed;width:0;height:0;overflow:hidden;pointer-events:none;';
		document.body.appendChild(anchor);
	}
	return anchor;
};

interface SpotlightProps {
    spotlightId: string;
	mediaId: number | string;
	position: number | string;
	isAudioOnly: boolean;
	nextShot?: boolean;
}

const Spotlight: React.FC<SpotlightProps> = ({
	spotlightId, position, isAudioOnly, nextShot,
}) => {
	const myRef = useRef<HTMLDivElement | null>(null);
	const [unmanagedDiv, setUnmanagedDiv] = useState<HTMLDivElement | null>(null);
	const [allContainers, setAllContainers] = useState<{ [key: string]: Element }>({});

	const hiddenSlotId = `${nextShot ? '-hidden-spotlight-' : ''}`;
	const containerID = `${position}${hiddenSlotId}${isAudioOnly ? 'audio-only' : ''}`;

	useEffect(() => {
		const containers = document.querySelectorAll('.spotlight-container');
		containers.forEach((container) => {
			allContainers[container.id] = container;
			setAllContainers(allContainers);
		});
	}, []);

	useEffect(() => {
		const divExists = document.getElementById(spotlightId);
		let createdDiv: HTMLDivElement;
		if (divExists) {
			createdDiv = divExists as HTMLDivElement;
		} else {
			createdDiv = document.createElement('div');
			createdDiv.className = 'spotlight';
			createdDiv.id = spotlightId;
		}

		setUnmanagedDiv(createdDiv);
		return () => {
			createdDiv.remove();
		};
	}, []);

	// Audio-only: keep unmanagedDiv in a stable anchor so it's never moved between containers.
	// Moving a DOM node with a playing <audio> pauses it; position in DOM is irrelevant for audio.
	useEffect(() => {
		if (!unmanagedDiv || !isAudioOnly) return;
		getAudioAnchor().appendChild(unmanagedDiv);
	}, [unmanagedDiv, isAudioOnly]);

	// Video: move unmanagedDiv to the correct spotlight container when containerID changes.
	// Playback restoration after the DOM move is handled by use-sync-media (mediaEnteredCanvas).
	useEffect(() => {
		if (!myRef.current || !unmanagedDiv || isAudioOnly) return;
		myRef.current.appendChild(unmanagedDiv);
	}, [myRef.current, unmanagedDiv, containerID, isAudioOnly]);

	return (allContainers[containerID]
        && <>
            {ReactDOM.createPortal(
				<div
					key={`spotlight-holder-${spotlightId}`}
					id={`spotlight-holder-${spotlightId}`}
					className='spotlight'
					ref={myRef}
				></div>,
				allContainers[containerID]
			)}
        </>
	);
};

export default memo(Spotlight);
